import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Row, Col, Button, Typography, Space, Spin, Alert, Card, Modal, message, Form, Select, Radio, Tag, theme, Dropdown,
} from 'antd'
import type { MenuProps } from 'antd'
import VoiceTextarea from '@/components/VoiceTextarea'
import { tornaIndietro } from '@/lib/navigazione'
import {
  ArrowLeftOutlined,
  PrinterOutlined,
  DeleteOutlined,
  StopOutlined,
  UndoOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  DownOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { usePageTitle } from '@/contexts/PageTitleContext'
import { useWorkOrder, useDeleteWorkOrder } from '@/hooks/useWorkOrders'
import { useQueryClient } from '@tanstack/react-query'
import axiosInstance from '@/lib/axios'
import type { WorkOrder } from '@/types'
import { useWOStatusTypesList } from '@/hooks/useSystemTables'
import { useRdasForWorkOrder } from '@/hooks/useRdaIntervento'
import { printWorkOrder } from '@/components/WorkOrderPrintView'
import type { PrintPhoto } from '@/components/WorkOrderPrintView'
import { usePermission } from '@/hooks/usePermission'

import PhaseProgressBar from '@/components/scheda-v2/PhaseProgressBar'
import FaseAccettazione from '@/components/scheda-v2/FaseAccettazione'
import AccettazioneStrip from '@/components/scheda-v2/AccettazioneStrip'
import FaseAnalisi from '@/components/scheda-v2/FaseAnalisi'
import PhaseCard from '@/components/scheda-v2/PhaseCard'
import PagamentiPanel from '@/components/scheda-v2/PagamentiPanel'
import CostiPanel from '@/components/scheda-v2/CostiPanel'
import { PhotoGallery } from '@/components/foto/PhotoGallery'
import { PhotoCaptureManager, PhotoCaptureManagerHandle } from '@/components/PhotoCaptureManager'
import { tonoScheda, useColoriTono } from '@/components/scheda-v3/colori'


const { Title, Text } = Typography

import { toAxiosPrintPath, resizeImageForPrint } from '@/components/shared/printHelpers'
import type { PrintOption } from '@/components/shared/printHelpers'

// Frasi descrittive per ogni stato della scheda (registro attivo, senza gergo)
const FRASI_STATO: Record<string, string> = {
  accettazione:      'Scheda in fase di accettazione — inserisci i dati del cliente, del veicolo e la descrizione del danno.',
  'in analisi':      'Il meccanico sta esaminando il veicolo e registrando gli interventi necessari.',
  'in approvazione': 'Offerta inviata al cliente — la scheda avanza dopo l\'approvazione del preventivo.',
  approvata:         'Preventivo approvato — in attesa dell\'avvio della lavorazione.',
  'in lavorazione':  'Lavorazione in corso — avanza a "Completata" quando tutti gli interventi sono terminati.',
  sospesa:           'Lavorazione sospesa — riprendi quando sei pronto a procedere.',
  completata:        'Lavori terminati — in attesa di riconsegna al cliente.',
  riconsegnata:      'Veicolo riconsegnato al cliente.',
  annullata:         'Scheda annullata — puoi ripristinarla dalla toolbar.',
}

// Mappa stato → componente fase (necessariamente in codice)
const FASE_COMPONENTS: Record<string, string> = {
  'accettazione':    'accettazione',
  'in analisi':      'in analisi',
  'in approvazione': 'in approvazione',
  'approvata':       'approvata',
  'in lavorazione':  'in lavorazione',
  'completata':      'completata',
  'riconsegnata':    'riconsegnata',
}

interface UserOption {
  value: number
  label: string
}

const SchedaLavoroV2: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const isNew = id === 'new'
  const workOrderId = isNew ? 0 : Number(id)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const prefilledDate = searchParams.get('data_appuntamento') ?? ''
  const { setTitle } = usePageTitle()
  const queryClient = useQueryClient()

  const { data: statusTypes = [] } = useWOStatusTypesList()
  const { data: workOrder, isLoading, error, refetch } = useWorkOrder(workOrderId)
  const { data: workOrderRdas = [] } = useRdasForWorkOrder(isNew ? undefined : workOrderId)
  const deleteWO = useDeleteWorkOrder()
  const canCancel = usePermission('work_orders.cancel')
  const canDelete = usePermission('work_orders.delete')
  const canViewCosts    = usePermission('costs.view')
  const canViewPayments = usePermission('payments.view')
  const canEditWO = usePermission('work_orders.edit')
  const canViewPhotos   = usePermission('work_order_photos.view')

  // ── Stampa con foto ───────────────────────────────────────────────────────────
  const [printFetching, setPrintFetching] = useState(false)
  const [printModalOpen, setPrintModalOpen] = useState(false)
  const [printLoading, setPrintLoading] = useState(false)
  const [printPhotos, setPrintPhotos] = useState<any[]>([])
  const [printDataCache, setPrintDataCache] = useState<any[]>([])
  const [printOption, setPrintOption] = useState<PrintOption>('none')

  const handlePrintConfirm = useCallback(async () => {
    if (!workOrder) return
    setPrintLoading(true)
    try {
      let photosToDownload: any[] = []
      if (printOption === 'accettazione') {
        photosToDownload = printPhotos.filter((p: any) => p.fase === 'accettazione')
      } else if (printOption === 'all') {
        photosToDownload = printPhotos
      }
      // printOption === 'none' → nessuna foto, comportamento identico a oggi

      // Scarica originali, ridimensiona su canvas (max 1400px), skip le foto che falliscono
      const results = await Promise.allSettled(
        photosToDownload.map(async (photo: any): Promise<PrintPhoto> => {
          // google_drive_url → proxy /image-original (qualità piena)
          const path = toAxiosPrintPath(photo.google_drive_url ?? '')
          const res = await axiosInstance.get<Blob>(path, { responseType: 'blob' })
          const ct: string = (res.headers['content-type'] as string) ?? ''
          if (!ct.startsWith('image/')) throw new Error(`Non è un'immagine: ${ct}`)
          const dataUrl = await resizeImageForPrint(res.data)
          return {
            id: photo.id,
            fase: photo.fase,
            file_name: photo.file_name ?? '',
            data_url: dataUrl,
            created_at: photo.created_at,
          }
        })
      )
      const printPhotosData: PrintPhoto[] = results
        .filter((r): r is PromiseFulfilledResult<PrintPhoto> => r.status === 'fulfilled')
        .map(r => r.value)

      printWorkOrder(workOrder, workOrderRdas, printDataCache, printPhotosData)
      setPrintModalOpen(false)
    } catch (err) {
      console.error('Errore preparazione foto per stampa:', err)
    } finally {
      setPrintLoading(false)
    }
  }, [workOrder, workOrderRdas, printOption, printPhotos, printDataCache])

  // ── Acquisizione foto da riga intervento ─────────────────────────────────────
  const captureRef = useRef<PhotoCaptureManagerHandle>(null)
  const [captureInterventionId, setCaptureInterventionId] = useState<number | null>(null)
  const [captureModalOpen, setCaptureModalOpen] = useState(false)
  const [captureCommitting, setCaptureCommitting] = useState(false)

  type FaseFoto = 'accettazione' | 'lavorazione' | 'riconsegna'
  const FASE_FOTO_LABELS: Record<FaseFoto, string> = {
    accettazione: 'Accettazione',
    lavorazione: 'Lavorazione',
    riconsegna: 'Riconsegna',
  }
  const [captureFase, setCaptureFase] = useState<FaseFoto>('lavorazione')

  const openInterventionCapture = useCallback((interventionId: number) => {
    setCaptureInterventionId(interventionId)
    setCaptureFase('lavorazione')
    setCaptureModalOpen(true)
  }, [])

  const handleCommitCapture = useCallback(async () => {
    try {
      setCaptureCommitting(true)
      await captureRef.current?.commitPhotos()
      setCaptureModalOpen(false)
      queryClient.invalidateQueries({ queryKey: ['work-order-photos', workOrderId] })
    } catch {
      // commitPhotos mostra già il messaggio di errore
    } finally {
      setCaptureCommitting(false)
    }
  }, [queryClient, workOrderId])

  const handleCancelCapture = useCallback(() => {
    captureRef.current?.discardChanges()
    setCaptureModalOpen(false)
  }, [])

  const { token } = theme.useToken()
  const colori = useColoriTono(tonoScheda(workOrder?.stato))

  const [users, setUsers] = useState<UserOption[]>([])
  const [auditTrail, setAuditTrail] = useState<any[]>([])
  const [storicoAperto, setStoricoAperto] = useState(false)
  const [sospendiModalOpen, setSospendiModalOpen] = useState(false)
  const [sospendiLoading, setSospendiLoading] = useState(false)
  const [sospendiForm] = Form.useForm()
  const [annullaCommentoVisible, setAnnullaCommentoVisible] = useState(false)
  const [annullaCommentoLoading, setAnnullaCommentoLoading] = useState(false)
  const [annullaCommentoForm] = Form.useForm()
  const [isDirty, setIsDirty] = useState(false)

  useEffect(() => {
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  // Phases: exclude trasversali e stati senza componente
  const phases = statusTypes
    .filter(s => s.nome !== 'sospesa' && s.nome !== 'annullata' && FASE_COMPONENTS[s.nome])
    .sort((a, b) => a.ordine - b.ordine)

  const firstState = phases[0]

  useEffect(() => {
    if (isNew) setTitle('Nuova Scheda')
  }, [isNew])

  useEffect(() => {
    if (!isNew && workOrder) setTitle(`Scheda ${workOrder.numero_scheda}`)
    return () => { if (!isNew) setTitle('') }
  }, [workOrder, isNew])

  useEffect(() => {
    if (isNew || !canEditWO) return
    axiosInstance.get('/users/assegnabili').then(res => {
      const list: any[] = res.data.items || res.data || []
      setUsers(list.map((u: any) => ({
        value: u.id,
        label: `${u.nome || ''} ${u.cognome || ''}`.trim() || u.email,
      })))
    }).catch(() => setUsers([]))
  }, [isNew, canEditWO])

  useEffect(() => {
    if (isNew || !workOrderId) return
    axiosInstance.get(`/work-orders/${workOrderId}/audit-trail?limit=50`)
      .then(res => setAuditTrail(res.data.audit_trail || []))
      .catch(() => setAuditTrail([]))
  }, [workOrderId, workOrder?.stato, isNew])

  const handleStateChange = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['work-order', workOrderId] })
    refetch()
  }, [workOrderId])

  const handleWorkOrderUpdate = useCallback((patch: Partial<WorkOrder>) => {
    queryClient.setQueryData(['work-order', workOrderId], (old: any) => ({
      ...old,
      ...patch,
    }))
  }, [workOrderId])

  const handleInterventionsChange = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['work-order', workOrderId] })
    refetch()
  }, [workOrderId])

  const doAnnulla = useCallback(async (commentoStato?: string) => {
    try {
      setAnnullaCommentoLoading(true)
      await axiosInstance.post(`/work-orders/${workOrderId}/cancel`, commentoStato ? { commento_stato: commentoStato } : {})
      message.success('Scheda annullata')
      navigate('/schede-lavoro')
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Errore durante l\'annullamento')
    } finally {
      setAnnullaCommentoLoading(false)
      setAnnullaCommentoVisible(false)
      annullaCommentoForm.resetFields()
    }
  }, [workOrderId, navigate, annullaCommentoForm])

  const handleAnnulla = useCallback(() => {
    if (!workOrder) return
    const annullataType = statusTypes.find(s => s.nome === 'annullata')
    if (annullataType?.richiede_commento) {
      annullaCommentoForm.resetFields()
      setAnnullaCommentoVisible(true)
      return
    }
    Modal.confirm({
      title: 'Annullare la scheda?',
      icon: <span style={{ color: '#ff4d4f', fontSize: 22, marginRight: 8 }}>⚠</span>,
      content: `La scheda N. ${workOrder.numero_scheda} verrà annullata. Tutti i dati correlati saranno conservati e potrai ripristinarla in seguito.`,
      okText: 'Sì, annulla',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => new Promise<void>((resolve) => {
        Modal.confirm({
          title: 'Conferma definitiva',
          icon: <span style={{ color: '#ff4d4f', fontSize: 22, marginRight: 8 }}>⚠</span>,
          content: 'Stai per annullare questa scheda. Confermando, la scheda non sarà più visibile nella lista principale. Vuoi procedere?',
          okText: 'Conferma annullamento',
          okType: 'danger',
          cancelText: 'Annulla',
          onOk: async () => {
            try {
              await axiosInstance.post(`/work-orders/${workOrderId}/cancel`)
              message.success('Scheda annullata')
              navigate('/schede-lavoro')
            } catch (e: any) {
              message.error(e?.response?.data?.detail || 'Errore durante l\'annullamento')
            } finally {
              resolve()
            }
          },
          onCancel: () => resolve(),
        })
      }),
    })
  }, [workOrder, workOrderId, navigate, statusTypes, annullaCommentoForm])

  const handleRipristina = useCallback(async () => {
    try {
      const res = await axiosInstance.post(`/work-orders/${workOrderId}/restore`)
      message.success(`Scheda ripristinata (stato: ${res.data.restored_stato})`)
      refetch()
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Errore durante il ripristino')
    }
  }, [workOrderId, refetch])

  const handleSospendi = async (values: { reason: string }) => {
    setSospendiLoading(true)
    try {
      await axiosInstance.post(`/work-orders/${workOrderId}/transition/sospesa`, { reason: values.reason })
      setSospendiModalOpen(false)
      sospendiForm.resetFields()
      handleStateChange()
    } catch (e: any) {
      Modal.error({
        title: 'Errore',
        content: e?.response?.data?.detail || 'Impossibile sospendere la lavorazione.',
      })
    } finally {
      setSospendiLoading(false)
    }
  }

  const handleRiprendi = async () => {
    try {
      await axiosInstance.post(`/work-orders/${workOrderId}/transition/in lavorazione`)
      handleStateChange()
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Impossibile riprendere la lavorazione.')
    }
  }

  // ── NEW SCHEDA ───────────────────────────────────────────────────────────────
  if (isNew) {
    const draftWorkOrder = {
      id: 0,
      stato: (firstState?.nome ?? 'accettazione') as any,
      numero_scheda: '',
      customer_id: 0,
      vehicle_id: 0,
      data_compilazione: '',
      data_appuntamento: prefilledDate,
      valutazione_danno: '',
      created_at: '',
      interventions: [],
      priorita: 'media',
      sinistro: false,
    } as WorkOrder

    const currentOrder = firstState?.ordine ?? 1

    return (
      <div style={{ padding: '0 0 40px 0' }}>
        <Card
          style={{ marginBottom: 16, borderRadius: 0, borderLeft: 'none', borderRight: 'none', borderTop: 'none' }}
          styles={{ body: { padding: '12px 24px' } }}
        >
          <Row align="middle" justify="space-between">
            <Col>
              <Space align="center">
                {/* Stessa freccia del dettaglio, stesso comportamento: si torna
                    da dove si e' arrivati. `tornaIndietro` e' preferibile a un
                    navigate con `replace` perche' la voce precedente e' la lista
                    CON i suoi filtri, mentre un replace verso /schede-lavoro li
                    perderebbe e lascerebbe due voci identiche in cronologia.
                    In entrambi i casi il form abbandonato non resta raggiungibile
                    premendo indietro dalla lista. */}
                <Button
                  icon={<ArrowLeftOutlined />}
                  type="text"
                  title="Indietro"
                  onClick={() => tornaIndietro(navigate)}
                />
                <div>
                  <Title level={4} style={{ margin: 0 }}>Nuova Scheda di Lavoro</Title>
                  <Text type="secondary">Compila i dati di accettazione</Text>
                </div>
              </Space>
            </Col>
          </Row>
        </Card>

        <div style={{ padding: '0 24px', marginBottom: 20 }}>
          <PhaseProgressBar currentStato={firstState?.nome ?? 'accettazione'} statusTypes={statusTypes} />
        </div>

        <div style={{ padding: '0 24px' }}>
          {firstState && (
            <PhaseCard
              stato={firstState.nome}
              label={firstState.nome}
              color={firstState.colore}
              ordine={firstState.ordine}
              currentOrder={currentOrder}
            >
              <FaseAccettazione
                workOrder={draftWorkOrder}
                isEditable={true}
                isNew={true}
                createStato={firstState.nome}
                // `replace`: il form di creazione non e' un luogo a cui si torna,
                // e' una tappa che si consuma. La scheda ora esiste, e la sua
                // voce di cronologia prende il posto di quella del form: cosi'
                // l'Indietro dal dettaglio porta alla lista, non a un form
                // che non ha piu' nulla da creare.
                onCreated={(wo) => { setIsDirty(false); navigate(`/schede-lavoro/${wo.id}`, { replace: true }) }}
                onDirtyChange={setIsDirty}
              />
            </PhaseCard>
          )}
        </div>
      </div>
    )
  }

  // ── LOADING / ERROR ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    )
  }

  if (error || !workOrder) {
    return (
      <Alert
        type="error"
        message="Scheda non trovata"
        description="Impossibile caricare la scheda di lavoro richiesta."
        action={
          <Button onClick={() => navigate('/schede-lavoro')}>Torna alla lista</Button>
        }
      />
    )
  }

  // ── SCHEDA ESISTENTE ─────────────────────────────────────────────────────────

  const approvazioneEntry = auditTrail.find(e => e.to_state === 'approvata' || e.to_state === 'in lavorazione')
  const riconsegnaEntry   = auditTrail.find(e => e.to_state === 'riconsegnata')
  const rimandatoEntry    = auditTrail.find(e => e.to_state === 'accettazione' && e.from_state === 'in approvazione')
  const sospesiEntry      = auditTrail.find(e => e.to_state === 'sospesa')

  const interventionsCount = workOrder.interventions?.length ?? 0
  const hasDescrizione = Boolean(workOrder.valutazione_danno)
  const isFirstState = firstState ? workOrder.stato === firstState.nome : false
  const isDeletable = isFirstState

  const vehicleLabel = [workOrder.vehicle_marca, workOrder.vehicle_modello].filter(Boolean).join(' ')
  const targa = workOrder.vehicle_targa ? `(${workOrder.vehicle_targa})` : ''
  const customerName = workOrder.customer_nome || ''

  const handleDelete = () => {
    Modal.confirm({
      title: 'Elimina scheda',
      content: `Eliminare definitivamente la scheda N. ${workOrder.numero_scheda}? L'operazione non è reversibile.`,
      okText: 'Elimina',
      okType: 'danger',
      cancelText: 'Annulla',
      onOk: () => deleteWO.mutate(workOrderId, {
        // `replace`: la scheda e' stata cancellata davvero. Lasciare la sua voce
        // in cronologia significa che un Indietro la riapre e mostra
        // "Scheda non trovata".
        onSuccess: () => navigate('/schede-lavoro', { replace: true }),
      }),
    })
  }

  // ── Barra di stato: frase + dettaglio per ogni stato ────────────────────────
  const fraseStato = FRASI_STATO[workOrder.stato] ?? `Stato: ${workOrder.stato}.`

  let dettaglioStato: string | null = null
  if (workOrder.stato === 'in approvazione' && rimandatoEntry?.reason) {
    dettaglioStato = `Rimandato in accettazione: ${rimandatoEntry.reason}`
  } else if (workOrder.stato === 'approvata' && approvazioneEntry) {
    const dp  = approvazioneEntry.created_at ? dayjs(approvazioneEntry.created_at).format('DD/MM/YYYY HH:mm') : null
    const chi = approvazioneEntry.executor_name || null
    dettaglioStato = [dp && `Approvata il ${dp}`, chi && `da ${chi}`].filter(Boolean).join(' ') || null
  } else if (workOrder.stato === 'sospesa' && sospesiEntry?.reason) {
    dettaglioStato = sospesiEntry.reason
  } else if (workOrder.stato === 'completata') {
    const oreTotal = workOrder.interventions?.reduce((acc: number, i: any) => acc + (i.ore_effettive ?? 0), 0) ?? 0
    if (oreTotal > 0) dettaglioStato = `Ore effettive totali: ${oreTotal}h`
  } else if (workOrder.stato === 'riconsegnata' && riconsegnaEntry) {
    const dr  = riconsegnaEntry.created_at ? dayjs(riconsegnaEntry.created_at).format('DD/MM/YYYY HH:mm') : null
    const chi = riconsegnaEntry.executor_name || null
    dettaglioStato = [dr, chi].filter(Boolean).join(' · ') || null
  }

  // ── Menu "Altro" — azioni rare/distruttive ───────────────────────────────────
  const altroVoci: NonNullable<MenuProps['items']> = []
  if (canCancel && workOrder.stato !== 'annullata') {
    altroVoci.push({ key: 'annulla', label: 'Annulla scheda…', danger: true, icon: <StopOutlined />, onClick: handleAnnulla })
  }
  if (isDeletable && canDelete) {
    altroVoci.push({ key: 'elimina', label: 'Elimina scheda', danger: true, icon: <DeleteOutlined />, onClick: handleDelete })
  }

  return (
    <div style={{ padding: '0 0 40px 0' }}>
      {/* Header */}
      <Card
        className="scheda-header"
        style={{
          marginBottom: 0,
          borderRadius: 0,
          borderLeft: 'none',
          borderRight: 'none',
          borderTop: 'none',
        }}
        styles={{ body: { padding: '12px 24px' } }}
      >
        <Row align="middle" justify="space-between" wrap>
          <Col>
            <Space align="center">
              <Button
                className="no-print"
                icon={<ArrowLeftOutlined />}
                type="text"
                title="Indietro"
                onClick={() => tornaIndietro(navigate)}
              />
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  Scheda N. {workOrder.numero_scheda}
                </Title>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {customerName} {vehicleLabel && `· ${vehicleLabel}`} {targa}
                </Text>
              </div>
            </Space>
          </Col>
          <Col className="no-print">
            <Space wrap align="center">
              {canCancel && workOrder.stato === 'annullata' && (
                <Button
                  icon={<UndoOutlined />}
                  onClick={handleRipristina}
                >
                  Ripristina scheda
                </Button>
              )}
              <Button
                icon={<PrinterOutlined />}
                loading={printFetching}
                onClick={async () => {
                  if (!workOrder) return
                  setPrintFetching(true)
                  let printData: any[] = []
                  let fetchedPhotos: any[] = []
                  try {
                    const [pdRes, photosRes] = await Promise.allSettled([
                      axiosInstance.get(`/purchase-requests/work-order/${workOrderId}/print-data`),
                      axiosInstance.get(`/work-orders/${workOrderId}/photos`),
                    ])
                    if (pdRes.status === 'fulfilled') printData = pdRes.value.data
                    if (photosRes.status === 'fulfilled') fetchedPhotos = photosRes.value.data
                  } finally {
                    setPrintFetching(false)
                  }

                  if (fetchedPhotos.length === 0) {
                    // T13: scheda senza foto → stampa diretta, nessun modal
                    printWorkOrder(workOrder, workOrderRdas, printData)
                  } else {
                    setPrintPhotos(fetchedPhotos)
                    setPrintDataCache(printData)
                    setPrintOption('none')
                    setPrintModalOpen(true)
                  }
                }}
              >
                Stampa PDF
              </Button>
              {/* Dal 04/09/2026 i ruoli si sono invertiti: la V3 e' la pagina
                  predefinita e questa e' la rete di sicurezza. Il pulsante non
                  invita piu' a provare, riporta indietro (docs/parita-v2-v3.md). */}
              <Button
                size="small"
                // `replace` come sul pulsante gemello della V3: lo scambio fra
                // versioni non deve lasciare tracce in cronologia, altrimenti
                // l'Indietro ripercorre gli scambi invece di uscire dalla scheda.
                onClick={() => navigate(`/schede-lavoro/${id}`, { replace: true })}
              >
                Torna alla versione nuova
              </Button>
              {altroVoci.length > 0 && (
                <Dropdown menu={{ items: altroVoci }} trigger={['click']}>
                  <Button>
                    Altro <DownOutlined />
                  </Button>
                </Dropdown>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Barra workflow — cliccabile */}
      <div style={{ padding: '12px 24px 8px', borderBottom: '1px solid var(--ant-color-border, #e8e8e8)' }}>
        <PhaseProgressBar
          currentStato={workOrder.stato}
          statusTypes={statusTypes}
          workOrderId={workOrderId}
          interventionsCount={interventionsCount}
          hasDescrizione={hasDescrizione}
          onStateChange={handleStateChange}
        />
      </div>

      {/* Strip accettazione — sempre visibile */}
      <AccettazioneStrip
        workOrder={workOrder}
        onSaved={handleWorkOrderUpdate}
      />

      {/* Barra di stato — frase descrittiva, azioni di workflow, storico */}
      <div style={{
        margin: '12px 24px 0',
        padding: '10px 14px',
        background: colori.sfondo,
        borderLeft: `3px solid ${colori.bordo}`,
        borderRadius: token.borderRadius,
      }}>
        {/* Riga principale: frase + pulsanti di workflow */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <Text style={{ fontSize: 13, color: colori.testo }}>{fraseStato}</Text>
            {dettaglioStato && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
                {dettaglioStato}
              </Text>
            )}
          </div>
          {workOrder.stato === 'in lavorazione' && (
            <Button
              size="small"
              icon={<PauseCircleOutlined />}
              onClick={() => setSospendiModalOpen(true)}
            >
              Sospendi lavorazione
            </Button>
          )}
          {workOrder.stato === 'sospesa' && (
            <Button
              size="small"
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={handleRiprendi}
            >
              Riprendi lavorazione
            </Button>
          )}
        </div>

        {/* Storico cambi di stato — collassabile, chiuso di default */}
        {auditTrail.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <button
              onClick={() => setStoricoAperto(o => !o)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '2px 0',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Text type="secondary" style={{ fontSize: 12 }}>
                {storicoAperto ? '▾' : '▸'} Storico cambi di stato ({auditTrail.length})
              </Text>
            </button>
            {storicoAperto && (
              <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 5 }}>
                {auditTrail.map((e: any) => (
                  <div
                    key={e.id}
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 6,
                      paddingTop: 5,
                      borderTop: `1px solid ${token.colorBorderSecondary}`,
                    }}
                  >
                    <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                      {e.created_at ? dayjs(e.created_at).format('DD/MM/YY HH:mm') : '—'}
                    </Text>
                    <Text style={{ fontSize: 12 }}>
                      {e.from_state} → {e.to_state}
                    </Text>
                    {e.executor_name && (
                      <Text type="secondary" style={{ fontSize: 12 }}>{e.executor_name}</Text>
                    )}
                    {e.reason && (
                      <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                        ({e.reason})
                      </Text>
                    )}
                    <Tag
                      color={
                        e.transition_type === 'automatic' ? 'processing' :
                        e.transition_type === 'rollback'  ? 'warning' :
                        'default'
                      }
                      style={{ fontSize: 11, marginLeft: 'auto' }}
                    >
                      {e.transition_type === 'automatic' ? 'automatico' :
                       e.transition_type === 'rollback'  ? 'rollback' :
                       'manuale'}
                    </Tag>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Commento stato / storico annullamento */}
      {(workOrder.commento_annullamento && workOrder.data_ripristino) ? (
        <div style={{ padding: '12px 24px 0' }}>
          <Alert
            type="warning"
            showIcon
            message={
              <span>
                Scheda annullata il{' '}
                <strong>{dayjs(workOrder.data_annullamento).format('DD/MM/YYYY HH:mm')}</strong>
                {' '}per: <em>{workOrder.commento_annullamento}</em>
                {' — '}ripristinata il{' '}
                <strong>{dayjs(workOrder.data_ripristino).format('DD/MM/YYYY HH:mm')}</strong>
              </span>
            }
          />
        </div>
      ) : workOrder.stato === 'annullata' && workOrder.commento_stato ? (
        <div style={{ padding: '12px 24px 0' }}>
          <Alert
            type="warning"
            showIcon
            message={<span><strong>Commento stato:</strong> {workOrder.commento_stato}</span>}
          />
        </div>
      ) : null}

      {/* Area lavoro: analisi + interventi + RDA/RDO */}
      <div style={{ paddingTop: 16 }}>
        <FaseAnalisi
          workOrder={workOrder}
          users={users}
          canEditWO={canEditWO}
          onWorkOrderUpdate={handleWorkOrderUpdate}
          onInterventionsChange={handleInterventionsChange}
          onPhotoCaptureIntervention={openInterventionCapture}
        />
      </div>

      {/* Galleria foto */}
      {canViewPhotos && !isNew && (
        <div style={{ paddingTop: 16 }}>
          <PhotoGallery
            workOrderId={workOrderId as number}
            interventions={workOrder.interventions ?? []}
          />
        </div>
      )}

      {/* Modale acquisizione foto da riga intervento */}
      <Modal
        title={
          <span>
            Foto intervento
            {captureInterventionId != null
              ? ` — intervento #${captureInterventionId}`
              : ''}
            {' '}
            <Select
              size="small"
              value={captureFase}
              onChange={(v) => setCaptureFase(v as FaseFoto)}
              style={{ marginLeft: 8, width: 140 }}
              options={Object.entries(FASE_FOTO_LABELS).map(([k, label]) => ({ value: k, label }))}
            />
          </span>
        }
        open={captureModalOpen}
        onCancel={handleCancelCapture}
        onOk={handleCommitCapture}
        confirmLoading={captureCommitting}
        okText="Salva foto"
        cancelText="Annulla"
        width={520}
        destroyOnClose
      >
        <PhotoCaptureManager
          ref={captureRef}
          workOrderId={workOrderId as number}
          fase={captureFase}
          interventionId={captureInterventionId}
          compact={false}
          readOnly={false}
        />
      </Modal>

      {/* Modale opzioni stampa foto */}
      <Modal
        title="Opzioni di stampa"
        open={printModalOpen}
        onCancel={() => { if (!printLoading) { setPrintModalOpen(false) } }}
        onOk={handlePrintConfirm}
        confirmLoading={printLoading}
        okText={printLoading ? 'Preparazione foto…' : 'Stampa'}
        cancelText="Annulla"
        cancelButtonProps={{ disabled: printLoading }}
        maskClosable={!printLoading}
        width={420}
      >
        <Radio.Group
          value={printOption}
          onChange={e => setPrintOption(e.target.value as PrintOption)}
          style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '8px 0' }}
        >
          <Radio value="none">
            <strong>Senza foto</strong>
            <span style={{ color: '#999', marginLeft: 6 }}>— stampa identica a oggi</span>
          </Radio>
          <Radio value="accettazione">
            Solo accettazione
            <span style={{ marginLeft: 8, color: '#1890ff', fontWeight: 600 }}>
              ({printPhotos.filter((p: any) => p.fase === 'accettazione').length} foto)
            </span>
          </Radio>
          <Radio value="all">
            Tutte le foto
            <span style={{ marginLeft: 8, color: '#1890ff', fontWeight: 600 }}>
              ({printPhotos.length} foto)
            </span>
          </Radio>
        </Radio.Group>
      </Modal>

      {canViewCosts && (
        <div style={{ paddingTop: 16 }}>
          <CostiPanel workOrderId={workOrderId} />
        </div>
      )}

      {canViewPayments && (
        <div style={{ paddingTop: 16 }}>
          <PagamentiPanel workOrderId={workOrderId} />
        </div>
      )}

      <Modal
        title="Motivo sospensione"
        open={sospendiModalOpen}
        onCancel={() => { setSospendiModalOpen(false); sospendiForm.resetFields() }}
        onOk={() => sospendiForm.submit()}
        confirmLoading={sospendiLoading}
        okText="Sospendi"
        cancelText="Annulla"
      >
        <Form form={sospendiForm} onFinish={handleSospendi}>
          <Form.Item
            name="reason"
            rules={[{ required: true, message: 'Inserisci il motivo della sospensione' }]}
          >
            <VoiceTextarea rows={4} minHeight={100} maxHeight={220} placeholder="Es. in attesa di ricambi, cliente non disponibile..." label="Motivo sospensione" compact />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Commento annullamento"
        open={annullaCommentoVisible}
        onCancel={() => { setAnnullaCommentoVisible(false); annullaCommentoForm.resetFields() }}
        onOk={() => annullaCommentoForm.submit()}
        confirmLoading={annullaCommentoLoading}
        okText="Annulla scheda"
        okButtonProps={{ danger: true }}
        cancelText="Indietro"
      >
        <Alert
          type="warning"
          showIcon
          message={`La scheda N. ${workOrder?.numero_scheda} verrà annullata`}
          description="Inserisci un commento obbligatorio per procedere con l'annullamento."
          style={{ marginBottom: 16 }}
        />
        <Form form={annullaCommentoForm} onFinish={v => doAnnulla(v.commento_stato)}>
          <Form.Item
            name="commento_stato"
            rules={[{ required: true, message: 'Inserisci un commento' }, { min: 3, message: 'Almeno 3 caratteri' }]}
          >
            <VoiceTextarea rows={4} minHeight={100} maxHeight={220} placeholder="Motivo annullamento..." label="Commento" compact />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default SchedaLavoroV2
